import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Ban,
  CheckCircle2,
  ChevronDown,
  Pencil,
  Power,
  PowerOff,
  Search,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ConfirmModal from "../components/ConfirmModal";
import { UsersService } from "../../../services/users.service";

const ADMIN_USERS_FETCH_LIMIT = 100000;

function SummaryCard({ title, value, tone = "default" }) {
  const toneClasses =
    tone === "success"
      ? "border-[#d7f0cf] bg-[#f5fff2] text-[#59a647]"
      : tone === "danger"
        ? "border-[#f8d7dc] bg-[#fff6f7] text-[#d86a78]"
        : tone === "warning"
          ? "border-[#ffe9bf] bg-[#fff9ec] text-[#b98917]"
          : "border-[#f2c6cc] bg-white text-[#d86a78]";

  return (
    <div className={`rounded-[16px] border px-4 py-4 ${toneClasses}`}>
      <div className="text-[12px] font-medium uppercase tracking-wide">{title}</div>
      <div className="mt-2 text-[28px] font-bold leading-none">{value}</div>
    </div>
  );
}

function StatusPill({ status, isActive }) {
  const normalized = String(status || "").toUpperCase();

  if (normalized === "BLOCKED") {
    return (
      <span className="inline-flex rounded-full bg-[#fff1f3] px-3 py-1 text-[11px] font-semibold text-[#d86a78]">
        Blocked
      </span>
    );
  }

  if (isActive === false || normalized === "INACTIVE") {
    return (
      <span className="inline-flex rounded-full bg-[#fff9ec] px-3 py-1 text-[11px] font-semibold text-[#b98917]">
        Inactive
      </span>
    );
  }

  return (
    <span className="inline-flex rounded-full bg-[#eef9ea] px-3 py-1 text-[11px] font-semibold text-[#59a647]">
      Active
    </span>
  );
}

function SelectLike({ value, onChange, placeholder, options = [] }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const normalizedOptions = [
    { value: "", label: placeholder },
    ...options.map((option) => ({
      value: String(option?.value ?? ""),
      label: String(option?.label ?? option?.value ?? ""),
    })),
  ];

  const selected =
    normalizedOptions.find((option) => String(option.value) === String(value)) ||
    normalizedOptions[0];

  useEffect(() => {
    const handleOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  return (
    <div ref={wrapperRef} className="relative h-[38px]">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex h-[38px] w-full items-center justify-between rounded-[8px] border bg-white px-4 text-left text-[12px] outline-none transition ${
          open
            ? "border-[#d86a78] ring-2 ring-[#f7d7db]"
            : "border-[#f2c6cc] hover:border-[#d86a78]"
        }`}
      >
        <span className="min-w-0 flex-1 truncate text-slate-500">
          {selected?.label || placeholder}
        </span>
        <ChevronDown
          size={15}
          className={`ml-2 shrink-0 text-slate-400 transition ${
            open ? "rotate-180 text-[#d86a78]" : ""
          }`}
        />
      </button>

      {open ? (
        <div className="absolute left-0 right-0 top-[44px] z-[200] max-h-[250px] overflow-y-auto rounded-[12px] border border-[#f2c6cc] bg-white p-1 shadow-[0_18px_45px_rgba(15,23,42,0.16)]">
          {normalizedOptions.map((option) => {
            const active = String(option.value) === String(value);

            return (
              <button
                key={`${placeholder}-${option.value || "all"}`}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={`block w-full rounded-[9px] px-3 py-2.5 text-left text-[12px] transition ${
                  active
                    ? "bg-[#fff1f3] font-semibold text-[#d86a78]"
                    : "text-slate-700 hover:bg-[#fff7f8] hover:text-[#d86a78]"
                }`}
              >
                <span className="block truncate">{option.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default function UserDetails() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const getPositiveNumber = (value, fallback) => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState(() => searchParams.get("q") || "");
  const [status, setStatus] = useState(() => searchParams.get("status") || "");
  const [profileCompleted, setProfileCompleted] = useState(
    () => searchParams.get("profile") || ""
  );
  const [page, setPage] = useState(() => getPositiveNumber(searchParams.get("page"), 1));
  const [pageSize, setPageSize] = useState(() =>
    getPositiveNumber(searchParams.get("limit"), 10)
  );
  const [confirmState, setConfirmState] = useState({
    open: false,
    kind: "",
    row: null,
  });
  const [infoModal, setInfoModal] = useState({
    open: false,
    type: "info",
    title: "",
    description: "",
  });
  const [saving, setSaving] = useState(false);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await UsersService.list({ page: 1, limit: ADMIN_USERS_FETCH_LIMIT });
      setItems(Array.isArray(res?.items) ? res.items : []);
    } catch (e) {
      setError(e?.message || "Failed to load users");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();

    if (page > 1) params.set("page", String(page));
    if (pageSize !== 10) params.set("limit", String(pageSize));
    if (q.trim()) params.set("q", q.trim());
    if (status) params.set("status", status);
    if (profileCompleted) params.set("profile", profileCompleted);

    setSearchParams(params, { replace: true });
  }, [page, pageSize, q, status, profileCompleted, setSearchParams]);

  const filtered = useMemo(() => {
    const search = q.trim().toLowerCase();

    return items.filter((item) => {
      const normalizedStatus = String(item.status || "").toUpperCase();
      const computedStatus =
        normalizedStatus === "BLOCKED"
          ? "BLOCKED"
          : item.isActive === false || normalizedStatus === "INACTIVE"
            ? "INACTIVE"
            : "ACTIVE";

      const matchSearch = search
        ? [item.name, item.email, item.phone, item.userCode, item.city, item.state]
            .join(" ")
            .toLowerCase()
            .includes(search)
        : true;

      const matchStatus = status ? computedStatus === String(status) : true;

      const matchProfile =
        profileCompleted === ""
          ? true
          : String(item.profileCompleted) === String(profileCompleted === "true");

      return matchSearch && matchStatus && matchProfile;
    });
  }, [items, q, status, profileCompleted]);

  const summary = useMemo(() => {
    const total = items.length;
    const blocked = items.filter((x) => String(x.status || "").toUpperCase() === "BLOCKED").length;
    const inactive = items.filter(
      (x) => String(x.status || "").toUpperCase() !== "BLOCKED" && x.isActive === false
    ).length;
    const active = total - blocked - inactive;
    const completed = items.filter((x) => x.profileCompleted).length;

    return { total, active, inactive, blocked, completed };
  }, [items]);

  const total = filtered.length;

  const totalPages = useMemo(() => {
    if (total <= 0) return 1;
    return Math.max(1, Math.ceil(total / pageSize));
  }, [total, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    if (total <= 0) return;

    const nextPageSize =
      total < 10 ? total : pageSize > total ? total : pageSize < 10 ? 10 : pageSize;

    if (nextPageSize !== pageSize) {
      setPage(1);
      setPageSize(nextPageSize);
    }
  }, [total, pageSize]);

  const pageSizeOptions = useMemo(() => {
    if (total <= 0) return [10];

    const base = [10, 25, 50, 100, 250, 500];
    const opts = base.filter((n) => n <= total);

    if (!opts.includes(total)) opts.push(total);

    return Array.from(new Set(opts)).sort((a, b) => a - b);
  }, [total]);

  const onChangePageSize = (value) => {
    const n = Number(value);
    setPage(1);
    setPageSize(Number.isFinite(n) && n > 0 ? n : 10);
  };

  const paginatedUsers = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const canPrev = page > 1;
  const canNext = page < totalPages;
  const startIndex = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIndex = total === 0 ? 0 : Math.min(page * pageSize, total);

  const closeConfirm = () => setConfirmState({ open: false, kind: "", row: null });

  const closeInfo = () =>
    setInfoModal({
      open: false,
      type: "info",
      title: "",
      description: "",
    });

  const getConfirmTitle = () => {
    const name = confirmState.row?.name || "this user";

    if (confirmState.kind === "block") return `Block ${name}?`;
    if (confirmState.kind === "unblock") return `Unblock ${name}?`;
    if (confirmState.kind === "deactivate") return `Deactivate ${name}?`;
    if (confirmState.kind === "activate") return `Activate ${name}?`;

    return "Confirm action?";
  };

  const getConfirmDescription = () => {
    if (confirmState.kind === "block") {
      return "This account will be blocked. The user cannot login, and the same email/phone cannot be used to create a new account until unblocked.";
    }

    if (confirmState.kind === "unblock") {
      return "This account will be unblocked. The user can login again with the same email/phone.";
    }

    if (confirmState.kind === "deactivate") {
      return "This account will be temporarily deactivated. Admin can activate it again later.";
    }

    if (confirmState.kind === "activate") {
      return "This account will be activated again. The user can login normally.";
    }

    return "";
  };

  const handleConfirm = async () => {
    if (!confirmState?.row || saving) return;

    try {
      setSaving(true);
      const userName = confirmState.row.name || "User";

      if (confirmState.kind === "block") {
        await UsersService.block(confirmState.row.id);
        setInfoModal({
          open: true,
          type: "success",
          title: "User blocked",
          description: `${userName} has been blocked successfully.`,
        });
      }

      if (confirmState.kind === "unblock") {
        await UsersService.unblock(confirmState.row.id);
        setInfoModal({
          open: true,
          type: "success",
          title: "User unblocked",
          description: `${userName} can use the app again.`,
        });
      }

      if (confirmState.kind === "activate") {
        await UsersService.activate(confirmState.row.id);
        setInfoModal({
          open: true,
          type: "success",
          title: "User activated",
          description: `${userName} has been activated successfully.`,
        });
      }

      if (confirmState.kind === "deactivate") {
        await UsersService.deactivate(confirmState.row.id);
        setInfoModal({
          open: true,
          type: "success",
          title: "User deactivated",
          description: `${userName} has been deactivated successfully.`,
        });
      }

      closeConfirm();
      await loadUsers();
    } catch (e) {
      setInfoModal({
        open: true,
        type: "error",
        title: "Action failed",
        description: e?.message || "Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full">
      <ConfirmModal
        open={confirmState.open}
        icon={confirmState.kind === "block" ? "ban" : "success"}
        title={getConfirmTitle()}
        description={getConfirmDescription()}
        onClose={closeConfirm}
        onConfirm={handleConfirm}
        confirmText={saving ? "PLEASE WAIT..." : "YES"}
        cancelText="NO"
      />

      <ConfirmModal
        open={infoModal.open}
        variant={infoModal.type === "error" ? "danger" : "info"}
        icon={infoModal.type === "success" ? "success" : "x"}
        title={infoModal.title}
        description={infoModal.description}
        onClose={closeInfo}
      />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <h1 className="text-[25px] font-bold tracking-wide text-[#d86a78]">USER DETAILS</h1>
        <div className="rounded-[10px] border border-[#f2c6cc] bg-[#fff8f9] px-4 py-2 text-[13px] font-semibold text-[#d86a78]">
          {filtered.length} Visible Users
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        <SummaryCard title="Total Users" value={summary.total} />
        <SummaryCard title="Active" value={summary.active} tone="success" />
        <SummaryCard title="Inactive" value={summary.inactive} tone="warning" />
        <SummaryCard title="Blocked" value={summary.blocked} tone="danger" />
        <SummaryCard title="Profile Completed" value={summary.completed} tone="warning" />
      </div>

      <div className="mt-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative w-full sm:w-[260px]">
            <input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              placeholder="Search user"
              className="h-[38px] w-full rounded-[8px] border border-[#f2c6cc] bg-white pl-10 pr-4 text-[12px] text-slate-600 outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
            />
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300"
            />
          </div>

          <div className="grid w-full grid-cols-1 gap-3 sm:w-[460px] sm:grid-cols-2">
            <SelectLike
              value={status}
              onChange={(value) => {
                setStatus(value);
                setPage(1);
              }}
              placeholder="Status"
              options={[
                { value: "ACTIVE", label: "Active" },
                { value: "INACTIVE", label: "Inactive" },
                { value: "BLOCKED", label: "Blocked" },
              ]}
            />
            <SelectLike
              value={profileCompleted}
              onChange={(value) => {
                setProfileCompleted(value);
                setPage(1);
              }}
              placeholder="Profile"
              options={[
                { value: "true", label: "Completed" },
                { value: "false", label: "Pending" },
              ]}
            />
          </div>
        </div>
      </div>

      <div className="mt-5 overflow-hidden rounded-[18px] border border-[#f2c6cc] bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-[1100px] w-full">
            <thead>
              <tr className="border-b border-[#f6d9de] bg-[#fffafb] text-left text-[12px] font-semibold uppercase tracking-wide text-[#d86a78]">
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4">User Code</th>
                <th className="px-5 py-4">Name</th>
                <th className="px-5 py-4">Email</th>
                <th className="px-5 py-4">Phone</th>
                <th className="px-5 py-4">Location</th>
                <th className="px-5 py-4">Profile</th>
                <th className="px-5 py-4">Last Login</th>
                <th className="px-5 py-4 text-center">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-5 py-12 text-center text-sm text-slate-500">
                    Loading users...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="9" className="px-5 py-12 text-center text-sm text-[#d86a78]">
                    {error}
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-5 py-12 text-center text-sm text-slate-500">
                    No users found.
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((row) => {
                  const rowStatus = String(row.status || "").toUpperCase();
                  const isBlocked = rowStatus === "BLOCKED";
                  const isInactive = !isBlocked && row.isActive === false;

                  return (
                    <tr
                      key={row.id}
                      className="border-b border-slate-100 text-[13px] text-slate-600 last:border-b-0"
                    >
                      <td className="px-5 py-4">
                        <StatusPill status={row.status} isActive={row.isActive} />
                      </td>
                      <td className="px-5 py-4">{row.userCode}</td>
                      <td
                        className="cursor-pointer px-5 py-4 font-medium text-slate-700 hover:text-[#d86a78]"
                        onClick={() => navigate(`/admin/dashboard/users/${row.id}`)}
                      >
                        {row.name}
                      </td>
                      <td className="px-5 py-4">{row.email}</td>
                      <td className="px-5 py-4">{row.phone || "-"}</td>
                      <td className="px-5 py-4">
                        {[row.city, row.state].filter(Boolean).join(", ") || "-"}
                      </td>
                      <td className="px-5 py-4">{row.profileCompletedLabel}</td>
                      <td className="px-5 py-4">{row.lastLoginLabel}</td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-center gap-3 text-slate-500">
                          <button
                            type="button"
                            title="Edit"
                            className="transition hover:text-[#d86a78]"
                            onClick={() => navigate(`/admin/dashboard/users/${row.id}/edit`)}
                          >
                            <Pencil size={18} />
                          </button>

                          {!isBlocked && (
                            <button
                              type="button"
                              title={isInactive ? "Activate account" : "Deactivate account"}
                              className={`inline-flex h-8 w-8 items-center justify-center rounded-full border transition ${
                                isInactive
                                  ? "border-[#d7f0cf] bg-[#f5fff2] text-[#59a647] hover:text-[#4b8d3d]"
                                  : "border-[#ffe9bf] bg-[#fff9ec] text-[#b98917] hover:text-[#8a650f]"
                              }`}
                              onClick={() =>
                                setConfirmState({
                                  open: true,
                                  kind: isInactive ? "activate" : "deactivate",
                                  row,
                                })
                              }
                            >
                              {isInactive ? <Power size={18} /> : <PowerOff size={18} />}
                            </button>
                          )}

                          <button
                            type="button"
                            title={isBlocked ? "Unblock account" : "Block account"}
                            className={`inline-flex h-8 w-8 items-center justify-center rounded-full border transition ${
                              isBlocked
                                ? "border-[#d7f0cf] bg-[#f5fff2] text-[#59a647] hover:text-[#4b8d3d]"
                                : "border-[#f8d7dc] bg-[#fff6f7] text-[#d86a78] hover:text-[#b94c5a]"
                            }`}
                            onClick={() =>
                              setConfirmState({
                                open: true,
                                kind: isBlocked ? "unblock" : "block",
                                row,
                              })
                            }
                          >
                            {isBlocked ? <CheckCircle2 size={18} /> : <Ban size={18} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-3 rounded-[18px] border border-[#f2c6cc] bg-white px-4 py-4 text-[12px] text-slate-500 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-center sm:text-left">
          Showing {startIndex}-{endIndex} of {total}
        </div>

        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
          <div className="flex items-center justify-between gap-3 sm:justify-start">
            <span>Rows</span>
            <select
              value={pageSize}
              onChange={(e) => onChangePageSize(e.target.value)}
              className="h-9 min-w-[88px] rounded-[10px] border border-[#f2c6cc] bg-white px-3 text-[12px] text-slate-600 outline-none focus:border-[#d86a78]"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-between gap-2 sm:justify-start">
            <button
              type="button"
              onClick={() => canPrev && setPage((p) => p - 1)}
              disabled={!canPrev}
              className="rounded-[10px] border border-[#f2c6cc] px-4 py-2 font-semibold text-slate-600 disabled:opacity-50"
            >
              Prev
            </button>

            <span className="min-w-[86px] text-center">
              Page {page} / {totalPages}
            </span>

            <button
              type="button"
              onClick={() => canNext && setPage((p) => p + 1)}
              disabled={!canNext}
              className="rounded-[10px] border border-[#f2c6cc] px-4 py-2 font-semibold text-slate-600 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}